
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomers } from "../Cart/service/Customers.service";

const CustomerSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { item, quantity = 1 } = location.state || {};
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Keyboard selected customer index
  const [activeIndex, setActiveIndex] = useState(-1);

  // =====================================================
  // SEARCH API
  // =====================================================
  useEffect(() => {
    const value = search.trim();

    // Empty search
    if (!value) {
      setCustomers([]);
      setShowDropdown(false);
      setActiveIndex(-1);
      return;
    }

    // Reset keyboard selection when search changes
    setActiveIndex(-1);

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const list = await getCustomers(value);
        const customerList = Array.isArray(list) ? list : [];
        setCustomers(customerList);
        setShowDropdown(true);
        // Don't automatically select anything
        setActiveIndex(-1);
      } catch (error) {
        console.error("Customer Search Error:", error);
        setCustomers([]);
        setShowDropdown(true);
        setActiveIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // =====================================================
  // OUTSIDE CLICK
  // =====================================================
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  // =====================================================
  // SCROLL ACTIVE CUSTOMER INTO VIEW
  // =====================================================
  useEffect(() => {
    if (activeIndex < 0) return;

    const activeElement = document.getElementById(
      `customer-option-${activeIndex}`
    );

    if (activeElement) {
      activeElement.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  // =====================================================
  // HIGHLIGHT SEARCH WORDS
  // =====================================================
  const highlightText = (text, searchValue) => {
    if (!text || !searchValue) {
      return text;
    }

    const words = searchValue
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) {
      return text;
    }

    const escapedWords = words.map((word) =>
      word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );

    const regex = new RegExp(
      `(${escapedWords.join("|")})`,
      "gi"
    );

    return String(text)
      .split(regex)
      .map((part, index) => {
        const isMatch = words.some(
          (word) =>
            part.toLowerCase() === word.toLowerCase()
        );

        return isMatch ? (
          <mark
            key={index}
            style={{
              color: "#000",
              fontWeight: 700,
              padding: "1px 2px",
              borderRadius: "3px",
              backgroundColor: "transparent",
            }}
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={index}>
            {part}
          </React.Fragment>
        );
      });
  };

  // =====================================================
  // CUSTOMER SELECT
  // =====================================================
  const handleCustomerSelect = (customer) => {
    if (!customer?.id) return;

    // Save selected customer
    localStorage.setItem(
      "selectedCustomer",
      JSON.stringify(customer)
    );

    setShowDropdown(false);
    setActiveIndex(-1);

    // Go back to scanner
    navigate("/scanner", {
      state: {
        customer,
        item,
        quantity,
      },
    });
  };

  // =====================================================
  // KEYBOARD NAVIGATION
  // =====================================================
  const handleKeyDown = (event) => {
    // -----------------------------------------------
    // ESC
    // -----------------------------------------------
    if (event.key === "Escape") {
      event.preventDefault();

      setShowDropdown(false);
      setActiveIndex(-1);

      return;
    }

    // No customers
    if (!showDropdown || customers.length === 0) {
      return;
    }

    // -----------------------------------------------
    // ARROW DOWN
    // -----------------------------------------------
    if (event.key === "ArrowDown") {
      event.preventDefault();

      setActiveIndex((prevIndex) => {
        // First item
        if (prevIndex < 0) {
          return 0;
        }

        // Last item -> stay at last
        if (prevIndex >= customers.length - 1) {
          return customers.length - 1;
        }

        return prevIndex + 1;
      });

      return;
    }

    // -----------------------------------------------
    // ARROW UP
    // -----------------------------------------------
    if (event.key === "ArrowUp") {
      event.preventDefault();

      setActiveIndex((prevIndex) => {
        // If nothing selected -> select last
        if (prevIndex < 0) {
          return customers.length - 1;
        }

        // First item -> stay at first
        if (prevIndex <= 0) {
          return 0;
        }

        return prevIndex - 1;
      });

      return;
    }

    // -----------------------------------------------
    // ENTER
    // -----------------------------------------------
    if (event.key === "Enter") {
      event.preventDefault();

      // If active customer exists
      if (
        activeIndex >= 0 &&
        activeIndex < customers.length
      ) {
        handleCustomerSelect(customers[activeIndex]);
      }

      return;
    }
  };

  // =====================================================
  // MOUSE HOVER
  // =====================================================
  const handleMouseEnter = (index) => {
    setActiveIndex(index);
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div
      className="container py-4"
      style={{
        margin: "120px auto",
        height: "100%",
      }}
    >
      <div
        ref={wrapperRef}
        className="position-relative"
        style={{
          maxWidth: "500px",
          margin: "0 auto",
        }}
      >
        {/* =========================================
            SEARCH INPUT
        ========================================= */}
        <input
          ref={inputRef}
          type="search"
          className="form-control"
          placeholder="Search Customer..."
          value={search}
          autoFocus
          onFocus={() => {
            if (search.trim() && customers.length > 0) {
              setShowDropdown(true);
            }
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
        />

        {/* =========================================
            DROPDOWN
        ========================================= */}
        {showDropdown && (
          <div
            className="customer-dropdown"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: "0 0 8px 8px",
              boxShadow:
                "0 5px 15px rgba(0,0,0,0.12)",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {/* =====================================
                LOADING
            ===================================== */}
            {loading ? (
              <div className="text-center py-3">
                Searching...
              </div>
            ) : customers.length > 0 ? (
              customers.map((customer, index) => {
                const isActive = index === activeIndex;

                return (
                  <div
                    id={`customer-option-${index}`}
                    key={customer.id || index}
                    role="option"
                    aria-selected={isActive}
                    onClick={() =>
                      handleCustomerSelect(customer)
                    }
                    onMouseEnter={() =>
                      handleMouseEnter(index)
                    }
                    style={{
                      padding: "12px 15px",
                      cursor: "pointer",
                      borderBottom:
                        "1px solid #eee",

                      // Keyboard / mouse selected
                      backgroundColor: isActive
                        ? "#f1f1f1"
                        : "#fff",

                      transition:
                        "background-color 0.1s ease",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#222",
                      }}
                    >
                      {highlightText(
                        customer.accountName,
                        search
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              /* =====================================
                  NO CUSTOMER
              ===================================== */
              <div className="text-center py-3 text-muted">
                No customer found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerSearch;

